import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaStar, FaCheckCircle, FaTimesCircle, FaArrowRight } from 'react-icons/fa';

const Comparison: React.FC = () => {
  const [selectedCasinos, setSelectedCasinos] = useState<string[]>(['allbritishcasino', 'funcasino', 'playojo']);

  const casinos = [
    {
      id: 'allbritishcasino',
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      rating: 4.8,
      bonus: '100% up to £100',
      minDeposit: '£10',
      withdrawal: '24-48h',
      wagering: '35x',
      games: 1200,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/allbritishcasino'
    },
    {
      id: 'funcasino',
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      rating: 4.7,
      bonus: '200% up to £200',
      minDeposit: '£20',
      withdrawal: '48-72h',
      wagering: '40x',
      games: 1500,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/funcasino'
    },
    {
      id: 'playojo',
      name: 'PlayOJO',
      image: '/images/offers/playojo.webp',
      rating: 4.9,
      bonus: '50 Free Spins',
      minDeposit: '£10',
      withdrawal: '24h',
      wagering: '0x',
      games: 800,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/playojo'
    },
    {
      id: 'casinocasino',
      name: 'CasinoCasino',
      image: '/images/offers/casinocasino.webp',
      rating: 4.6,
      bonus: '£150 Welcome Bonus',
      minDeposit: '£10',
      withdrawal: '24-48h',
      wagering: '30x',
      games: 1000,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/casinocasino'
    },
    {
      id: 'mrplay',
      name: 'MrPlay',
      image: '/images/offers/mrplay.webp',
      rating: 4.7,
      bonus: '100% + 200 Spins',
      minDeposit: '£10',
      withdrawal: '24-48h',
      wagering: '35x',
      games: 1400,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/mrplay'
    },
    {
      id: 'partycasino',
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      rating: 4.6,
      bonus: '£100 Welcome Bonus',
      minDeposit: '£10',
      withdrawal: '24-48h',
      wagering: '30x',
      games: 1100,
      license: 'UKGC',
      mobile: true,
      liveChat: true,
      link: '/offers/partycasino'
    }
  ];

  const features = [
    { label: 'UK License', key: 'license' },
    { label: 'Welcome Bonus', key: 'bonus' },
    { label: 'Min Deposit', key: 'minDeposit' },
    { label: 'Withdrawal Time', key: 'withdrawal' },
    { label: 'Wagering', key: 'wagering' },
    { label: 'Game Count', key: 'games' },
    { label: 'Mobile App', key: 'mobile' },
    { label: 'Live Chat', key: 'liveChat' },
    { label: 'Rating', key: 'rating' },
  ];

  return (
    <>
      {/* JSON-LD SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Casino Comparison - SpinTitanium",
            "description": "Compare the best UK casino offers side by side. Find the perfect casino with our detailed comparison tool.",
            "url": "https://spintitanium.com/comparison"
          })
        }}
      />

      <div className="min-h-screen bg-titanium-950 pt-20 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient">Compare Casino Offers</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Side-by-side comparison of the top UK casinos. Find the perfect match for your gaming style.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="glass rounded-2xl p-6 border-2 border-titanium-800 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-titanium-800">
                  <th className="text-left py-4 px-4 text-gray-400 font-semibold">Feature</th>
                  {casinos.map((casino) => (
                    <th key={casino.id} className="text-center py-4 px-4 min-w-[200px]">
                      <div className="flex flex-col items-center gap-2">
                        <img
                          src={casino.image}
                          alt={casino.name}
                          className="h-12 w-auto"
                          loading="lazy"
                        />
                        <div className="text-white font-bold">{casino.name}</div>
                        <div className="flex items-center gap-1 text-yellow-400">
                          <FaStar className="fill-current" />
                          <span>{casino.rating}</span>
                        </div>
                        <Link
                          to={casino.link}
                          className="text-neon-blue hover:text-neon-purple text-sm flex items-center gap-1"
                        >
                          View Review
                          <FaArrowRight className="text-xs" />
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr
                    key={feature.key}
                    className={`border-b border-titanium-800 ${index % 2 === 0 ? 'bg-titanium-900/50' : ''}`}
                  >
                    <td className="py-4 px-4 text-gray-300 font-medium">{feature.label}</td>
                    {casinos.map((casino) => (
                      <td key={casino.id} className="py-4 px-4 text-center">
                        {feature.key === 'mobile' || feature.key === 'liveChat' ? (
                          casino[feature.key as keyof typeof casino] ? (
                            <FaCheckCircle className="text-green-400 mx-auto text-xl" />
                          ) : (
                            <FaTimesCircle className="text-red-400 mx-auto text-xl" />
                          )
                        ) : feature.key === 'rating' ? (
                          <div className="flex items-center justify-center gap-1 text-yellow-400">
                            <FaStar className="fill-current" />
                            <span className="text-white">{casino[feature.key as keyof typeof casino]}</span>
                          </div>
                        ) : (
                          <span className="text-white">
                            {casino[feature.key as keyof typeof casino]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {casinos.map((casino) => (
              <div
                key={casino.id}
                className="glass rounded-xl p-6 border-2 border-titanium-800 hover:border-neon-blue transition-all"
              >
                <img
                  src={casino.image}
                  alt={casino.name}
                  className="h-16 w-auto mb-4"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-white mb-2">{casino.name}</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Bonus:</span>
                    <span className="text-neon-blue font-semibold">{casino.bonus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Games:</span>
                    <span className="text-white">{casino.games}+</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Withdrawal:</span>
                    <span className="text-white">{casino.withdrawal}</span>
                  </div>
                </div>
                <Link
                  to={casino.link}
                  className="block w-full bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 text-center"
                >
                  Read Full Review
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Comparison;


